/** Control diario de planta (docs/DISENO_CONTROL_PLANTA.md). */
import { apiDownload, apiRequest } from "../../lib/api";

const BASE = "/api/v1/continuidad/plantas";

export type TipoPunto = "planta" | "red";

export interface Pozo {
  id: number;
  planta_id: number;
  nombre: string;
  activo: boolean;
  registra_caudal: boolean;
  registra_horometro: boolean;
  registra_niveles: boolean;
}

export interface PuntoCloro {
  id: number;
  planta_id: number;
  nombre: string;
  tipo: TipoPunto;
  ubicacion: string | null;
  orden: number;
  activo: boolean;
}

export interface Planta {
  id: number;
  nombre: string;
  ubicacion: string | null;
  activa: boolean;
  tiene_electricidad: boolean;
  tiene_dosificador: boolean;
  pozos: Pozo[];
  puntos: PuntoCloro[];
}

export interface ConfigPlantas {
  cloro_min_mg_l: string;
  cloro_max_mg_l: string;
  plantas: Planta[];
}

export interface MedicionCloro {
  punto_id: number;
  punto_nombre: string;
  punto_tipo: TipoPunto;
  hora: string | null;
  cloro_mg_l: string;
  lugar: string | null;
  extra: boolean;
  fuera_de_rango: boolean;
}

export interface LecturaPozo {
  pozo_id: number;
  pozo_nombre: string;
  caudal_m3: string | null;
  horometro_h: string | null;
  nivel_estatico_m: string | null;
  nivel_dinamico_m: string | null;
  produccion_m3: string | null;
  horas_bombeo: string | null;
}

export interface RegistroPlanta {
  id: number;
  planta_id: number;
  fecha: string;
  operador_nombre: string | null;
  electricidad_kwh: string | null;
  energia_kwh: string | null;
  dosificador_frecuencia: string | null;
  dosificador_desplazamiento: string | null;
  observaciones: string | null;
  fuera_de_rango: boolean;
  accion_correctiva: string | null;
  revisado_at: string | null;
  produccion_m3: string | null;
  mediciones: MedicionCloro[];
  lecturas: LecturaPozo[];
}

export interface DatoMes {
  valor: string | null;
  origen: string | null;
}

export interface ResumenManual {
  gasto_cloro_kg: string | null;
  agua_facturada_m3: string | null;
  arranques: number | null;
  observaciones: string | null;
}

export interface ResumenMes {
  planta_id: number;
  planta_nombre: string;
  anio: number;
  mes: number;
  dias_registrados: number;
  dias_fuera_de_rango: number;
  produccion_m3: string;
  energia_kwh: string;
  nivel_estatico_prom_m: string | null;
  nivel_dinamico_prom_m: string | null;
  agua_facturada: DatoMes;
  arranques: DatoMes;
  gasto_cloro: DatoMes;
  observaciones: string | null;
  manual: ResumenManual;
  registros: RegistroPlanta[];
}

export interface PlantaInput {
  nombre: string;
  ubicacion?: string | null;
  tiene_electricidad: boolean;
  tiene_dosificador: boolean;
  pozos?: { nombre: string }[];
  puntos?: { nombre: string; tipo: TipoPunto }[];
}

export interface RegistroInput {
  operador_nombre?: string | null;
  electricidad_kwh?: string | null;
  dosificador_frecuencia?: string | null;
  dosificador_desplazamiento?: string | null;
  observaciones?: string | null;
  accion_correctiva?: string | null;
  mediciones: {
    punto_id: number;
    hora: string | null;
    cloro_mg_l: string;
    lugar: string | null;
    extra: boolean;
  }[];
  lecturas: {
    pozo_id: number;
    caudal_m3: string | null;
    horometro_h: string | null;
    nivel_estatico_m: string | null;
    nivel_dinamico_m: string | null;
  }[];
}

export const fetchConfigPlantas = () => apiRequest<ConfigPlantas>(BASE);
export const crearPlanta = (body: PlantaInput) =>
  apiRequest<Planta>(BASE, { method: "POST", body });
export const editarPlanta = (id: number, body: Partial<PlantaInput> & { activa?: boolean }) =>
  apiRequest<Planta>(`${BASE}/${id}`, { method: "PATCH", body });
export const crearPozo = (plantaId: number, nombre: string) =>
  apiRequest<Pozo>(`${BASE}/${plantaId}/pozos`, { method: "POST", body: { nombre } });
export const editarPozo = (id: number, body: Partial<Pozo>) =>
  apiRequest<Pozo>(`${BASE}/pozos/${id}`, { method: "PATCH", body });
export const crearPunto = (plantaId: number, nombre: string, tipo: TipoPunto) =>
  apiRequest<PuntoCloro>(`${BASE}/${plantaId}/puntos`, {
    method: "POST",
    body: { nombre, tipo },
  });
export const editarPunto = (id: number, body: Partial<PuntoCloro>) =>
  apiRequest<PuntoCloro>(`${BASE}/puntos/${id}`, { method: "PATCH", body });

export const fetchMes = (plantaId: number, anio: number, mes: number) =>
  apiRequest<ResumenMes>(`${BASE}/${plantaId}/mes/${anio}/${mes}`);
export const guardarRegistro = (plantaId: number, fecha: string, body: RegistroInput) =>
  apiRequest<RegistroPlanta>(`${BASE}/${plantaId}/registros/${fecha}`, { method: "PUT", body });
export const revisarRegistro = (registroId: number) =>
  apiRequest<RegistroPlanta>(`${BASE}/registros/${registroId}/revisar`, { method: "POST" });
export const guardarManual = (
  plantaId: number,
  anio: number,
  mes: number,
  body: ResumenManual,
) =>
  apiRequest<ResumenMes>(`${BASE}/${plantaId}/mes/${anio}/${mes}/manual`, {
    method: "PUT",
    body,
  });
export const descargarInforme = (
  plantaId: number,
  anio: number,
  mes: number,
  formato: "xlsx" | "pdf",
) =>
  apiDownload(
    `${BASE}/${plantaId}/mes/${anio}/${mes}/informe.${formato}`,
    `control-desinfeccion-${anio}-${String(mes).padStart(2, "0")}.${formato}`,
  );
export const guardarRangoCloro = (min: string, max: string) =>
  apiRequest<unknown>("/api/v1/empresa/configuracion", {
    method: "PATCH",
    body: { cloro_min_mg_l: min, cloro_max_mg_l: max },
  });
